import type { Category, EventId, PlanEvent } from "../../../shared/types";
import type { TemplateSlot } from "../utils/derive-template";
import { ScheduleSlotEditor } from "./ScheduleSlotEditor";
import { ScheduleAddSlotForm } from "./ScheduleAddSlotForm";
import { ContextMenu } from "../../../ui/atoms/ContextMenu";
import { ConfirmDialog } from "../../../ui/atoms/ConfirmDialog";

interface ScheduleModalsProps {
  readonly editingSlot: TemplateSlot | null;
  readonly addingForDay: number | null;
  readonly addingRecurrenceType?: "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly";
  readonly ctxMenu: { slot: TemplateSlot; anchorRect: DOMRect } | null;
  readonly deletingSlot: TemplateSlot | null;
  readonly defaultCategory: Category;
  readonly events: readonly PlanEvent[];
  readonly onEditSlot: (slot: TemplateSlot | null) => void;
  readonly onAddSlot: (day: number | null) => void;
  readonly onContextMenu: (menu: { slot: TemplateSlot; anchorRect: DOMRect } | null) => void;
  readonly onDeleteSlot: (slot: TemplateSlot | null) => void;
  readonly onUpdateEvent: (
    id: EventId,
    updates: Partial<
      Pick<PlanEvent, "title" | "startTime" | "endTime" | "category" | "description" | "tags">
    >,
  ) => void;
  readonly onAddEventFull: (data: Omit<PlanEvent, "id" | "createdAt">) => void;
  readonly onDeleteEvent: (id: EventId) => void;
}

export function ScheduleModals({
  editingSlot,
  addingForDay,
  addingRecurrenceType,
  ctxMenu,
  deletingSlot,
  defaultCategory,
  events,
  onEditSlot,
  onAddSlot,
  onContextMenu,
  onDeleteSlot,
  onUpdateEvent,
  onAddEventFull,
  onDeleteEvent,
}: ScheduleModalsProps) {
  // Find the full event for the editing slot
  const editingEvent = editingSlot ? events.find((e) => e.id === editingSlot.eventId) : undefined;

  return (
    <>
      {/* Modal editor */}
      {editingSlot && editingEvent && (
        <ScheduleSlotEditor
          slot={editingSlot}
          event={editingEvent}
          onSave={onUpdateEvent}
          onDelete={onDeleteEvent}
          onClose={() => onEditSlot(null)}
        />
      )}

      {/* Modal add form */}
      {addingForDay !== null && (
        <ScheduleAddSlotForm
          defaultDay={addingForDay}
          defaultRecurrenceType={addingRecurrenceType ?? "weekly"}
          defaultCategory={defaultCategory}
          onAdd={onAddEventFull}
          onClose={() => onAddSlot(null)}
        />
      )}

      {/* Context menu */}
      {ctxMenu && (
        <ContextMenu
          anchorRect={ctxMenu.anchorRect}
          onClose={() => onContextMenu(null)}
          items={[
            {
              label: "Modifier",
              onClick: () => {
                onEditSlot(ctxMenu.slot);
                onContextMenu(null);
              },
            },
            {
              label: "Supprimer",
              danger: true,
              onClick: () => {
                onDeleteSlot(ctxMenu.slot);
                onContextMenu(null);
              },
            },
          ]}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deletingSlot !== null}
        title="Supprimer ce creneau ?"
        message="Supprimer cet event recurrent et toutes ses occurrences ?"
        confirmLabel="Supprimer"
        variant="danger"
        onConfirm={() => {
          if (deletingSlot) onDeleteEvent(deletingSlot.eventId);
          onDeleteSlot(null);
        }}
        onCancel={() => onDeleteSlot(null)}
      />
    </>
  );
}
